import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import {
  XMarkIcon,
  CheckIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface Trainer {
  id: string;
  mg_nr?: string;
  member_id?: string;
  vorname: string;
  name: string;
}

interface EditEventModalProps {
  event: any;
  isOpen: boolean;
  onClose: () => void;
  refreshCalendar: () => void;
}

// Available colors matching design spec
const colorOptions = [
  '#5eb900', // Gruen
  '#F6BF26', // Gelb
  '#D50000', // Rot
  '#F4511E', // Orange
  '#E67C73', // Rosa
  '#33B679', // Tuerkis
  '#039BE5', // Blau
];

const EditEventModal: React.FC<EditEventModalProps> = ({ event, isOpen, onClose, refreshCalendar }) => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    color: '#5eb900',
    bookable: 3,
    trainer_id: '',
    co_trainer_id: '',
    start_time: '08:00',
    end_time: '09:00',
    taetigkeitsbericht: false,
    taetigkeitsberichtText: '',
  });
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<any>(null);

  useEffect(() => {
    if (isOpen && event) {
      const startTime = event?.start ? format(event.start, 'HH:mm') : '08:00';
      const endTime = event?.end ? format(event.end, 'HH:mm') : '09:00';

      const initialData = {
        color: event?.extendedProps?.color || colorOptions[0],
        bookable: event?.extendedProps?.bookable || 3,
        trainer_id: event?.extendedProps?.trainer_id || event?.extendedProps?.creator?.id || '',
        co_trainer_id: event?.extendedProps?.co_trainer_id || event?.extendedProps?.cotrainer?.id || '',
        start_time: startTime,
        end_time: endTime,
        taetigkeitsbericht: event?.extendedProps?.taetigkeitsbericht === 1 || event?.extendedProps?.taetigkeitsbericht === true,
        taetigkeitsberichtText: event?.extendedProps?.taetigkeitsbericht_text || '',
      };

      setFormData(initialData);
      setOriginalFormData(initialData);
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [isOpen, event]);

  useEffect(() => {
    const fetchTrainers = async () => {
      if (!isOpen) return;

      try {
        const response = await apiClient.get('/api/members/trainer');
        const mappedTrainers = response.data.map((trainer: any) => {
          const nameParts = trainer.name.trim().split(' ');
          const vorname = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : '';
          const name = nameParts.length > 1 ? nameParts[nameParts.length - 1] : trainer.name;
          return {
            id: trainer.mg_nr || trainer.member_id,
            mg_nr: trainer.mg_nr,
            member_id: trainer.member_id,
            vorname,
            name,
          };
        });
        setTrainers(mappedTrainers);
      } catch (err) {
        console.error('Error fetching trainers:', err);
      }
    };

    fetchTrainers();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  const hasChanges = () => {
    if (!originalFormData) return false;

    return (
      formData.color !== originalFormData.color ||
      formData.bookable !== originalFormData.bookable ||
      formData.trainer_id !== originalFormData.trainer_id ||
      formData.co_trainer_id !== originalFormData.co_trainer_id ||
      formData.start_time !== originalFormData.start_time ||
      formData.end_time !== originalFormData.end_time ||
      formData.taetigkeitsbericht !== originalFormData.taetigkeitsbericht ||
      formData.taetigkeitsberichtText !== originalFormData.taetigkeitsberichtText
    );
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!hasChanges()) {
      setError('Keine Aenderungen vorgenommen');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (!formData.trainer_id) {
      setError('Bitte waehlen Sie einen Trainer aus');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.patch(`/api/calendar/event/${event.id}`, {
        color: formData.color,
        bookable: formData.bookable,
        trainer_id: formData.trainer_id,
        co_trainer_id: formData.co_trainer_id || null,
        start_time: formData.start_time,
        end_time: formData.end_time,
        taetigkeitsbericht: formData.taetigkeitsbericht ? 1 : 0,
        taetigkeitsbericht_text: formData.taetigkeitsbericht ? formData.taetigkeitsberichtText : null,
      });

      if (response.data.success) {
        refreshCalendar();
        onClose();
      } else {
        setError(response.data.message || 'Fehler beim Bearbeiten des Termins');
      }
    } catch (err: any) {
      setError(`Fehler beim Bearbeiten: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentParticipants = event?.extendedProps?.bookingCount || 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 grid place-items-center flex-shrink-0">
            <PencilIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">
              {event?.title || 'Termin bearbeiten'}
            </h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">Termin bearbeiten</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-700 text-error-700 dark:text-error-300 rounded-xl text-sm flex items-center gap-2">
                <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* DATUM Section - Locked */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <CalendarIcon className="w-3.5 h-3.5" />
                Datum
              </h4>
              <div className="relative">
                <div className="flex items-center gap-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                  <CalendarIcon className="w-5 h-5 text-gray-400" />
                  <span className="flex-1 text-gray-500 dark:text-gray-400 font-medium">
                    {event?.start ? format(event.start, 'EEEE, dd. MMMM yyyy', { locale: de }) : 'Datum unbekannt'}
                  </span>
                  <div className="flex items-center gap-1">
                    <LockClosedIcon className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                  Datum kann nicht geaendert werden
                </p>
              </div>
            </div>

            {/* ZEITRAUM Section */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <ClockIcon className="w-3.5 h-3.5" />
                Zeitraum
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Von</label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Bis</label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* TRAINER Section */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <UserIcon className="w-3.5 h-3.5" />
                Trainer
              </h4>
              <select
                value={formData.trainer_id}
                onChange={(e) => setFormData({ ...formData, trainer_id: e.target.value })}
                className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="">Trainer auswaehlen</option>
                {trainers.map((trainer) => (
                  <option key={trainer.id} value={trainer.id}>
                    {trainer.vorname} {trainer.name}
                  </option>
                ))}
              </select>
            </div>

            {/* CO-TRAINER Section */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <UsersIcon className="w-3.5 h-3.5" />
                Co-Trainer
              </h4>
              <select
                value={formData.co_trainer_id}
                onChange={(e) => setFormData({ ...formData, co_trainer_id: e.target.value })}
                className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="">Kein Co-Trainer</option>
                {trainers.map((trainer) => (
                  <option key={trainer.id} value={trainer.id}>
                    {trainer.vorname} {trainer.name}
                  </option>
                ))}
              </select>
            </div>

            {/* KAPAZITAET Section */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <UserGroupIcon className="w-3.5 h-3.5" />
                Kapazitaet
              </h4>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Max. Teilnehmer</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={String(formData.bookable)}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setFormData({ ...formData, bookable: val === '' ? 0 : parseInt(val) });
                  }}
                  className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formData.bookable === 0 ? 'Termin nicht buchbar' : `${formData.bookable} Teilnehmer`}
                </p>
                {currentParticipants > 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                    <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                    {currentParticipants} Buchungen bereits vorhanden
                  </p>
                )}
              </div>
            </div>

            {/* TÄTIGKEITSBERICHT Section */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                Tätigkeitsbericht
              </h4>
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="edit-taetigkeitsbericht"
                    checked={formData.taetigkeitsbericht}
                    onChange={(e) => setFormData({ ...formData, taetigkeitsbericht: e.target.checked })}
                    className="w-4 h-4 text-amber-500 border-gray-300 dark:border-gray-600 rounded focus:ring-amber-500"
                  />
                  <label htmlFor="edit-taetigkeitsbericht" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Im Tätigkeitsbericht erwähnen
                  </label>
                </div>
                {formData.taetigkeitsbericht && (
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Beschreibung für Tätigkeitsbericht
                    </label>
                    <textarea
                      value={formData.taetigkeitsberichtText}
                      onChange={(e) => setFormData({ ...formData, taetigkeitsberichtText: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      rows={2}
                      placeholder="z.B. Jahreshauptversammlung mit Vorstandswahlen"
                      maxLength={500}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formData.taetigkeitsberichtText.length}/500 Zeichen
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* FARBE Section */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                Farbe
              </h4>
              <div className="flex justify-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    className={`w-10 h-10 rounded-full transition-all hover:scale-110 ${
                      formData.color === color ? 'ring-2 ring-offset-2 ring-gray-800 dark:ring-white' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold text-sm rounded-lg border border-gray-200 dark:border-gray-600 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !hasChanges()}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm rounded-lg transition-all disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <CheckIcon className="w-4 h-4" />
                Speichern
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEventModal;
